// @ts-nocheck
import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Pressable, ActivityIndicator, Platform } from 'react-native';
import { useLocalSearchParams, useRouter, Stack } from 'expo-router';
import { ArrowRight, Share2, Clock, Eye } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { supabase } from '@/utils/supabase';
import { ArticleSEO } from '@/components/seo/ArticleSEO';

interface Article {
  id: string;
  slug: string;
  title: string;
  meta_title: string;
  meta_description: string;
  category: string;
  hero_intro: string;
  tldr: string;
  main_content: Array<{ heading: string; content: string }>;
  common_mistakes: string;
  how_to_choose: string;
  kursi_connection: string;
  cta_text: string;
  related_articles: string[];
  keywords: string[];
  author: string;
  published_at: string;
  view_count: number;
}

export default function ArticlePage() {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const router = useRouter();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    if (!slug) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('articles')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (!data) {
        setError('המאמר לא נמצא');
        return;
      }

      setArticle(data as Article);

      await supabase
        .from('articles')
        .update({ view_count: (data.view_count || 0) + 1 })
        .eq('id', data.id);
    } catch (err) {
      console.error('Error loading article:', err);
      setError('שגיאה בטעינת המאמר');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!article) return;

    // SEO/GEO FIX: Share URLs must match canonical production domain for consistent indexing signals.
    const url = `https://kursiaii.com/articles/${article.slug}`;
    const text = `${article.title} - Kursi`;

    if (Platform.OS === 'web' && navigator.share) {
      try {
        await navigator.share({ title: article.title, text, url });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else if (Platform.OS === 'web') {
      navigator.clipboard.writeText(url);
      alert('הקישור הועתק ללוח');
    }
  };

  const handleCTA = () => {
    router.push('/chat');
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      </View>
    );
  }

  if (error || !article) {
    return (
      <View style={styles.container}>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error || 'המאמר לא נמצא'}</Text>
          <Pressable style={styles.backButton} onPress={() => router.back()}>
            <Text style={styles.backButtonText}>חזרה</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: article.meta_title || article.title,
          headerShown: false,
        }}
      />

      <ArticleSEO
        title={article.meta_title || article.title}
        description={article.meta_description || article.hero_intro}
        keywords={article.keywords || []}
        author={article.author}
        publishedAt={article.published_at}
        slug={article.slug}
      />

      <View style={styles.header}>
        <Pressable onPress={handleShare} style={styles.headerButton}>
          <Share2 color="#F8FAFC" size={22} />
        </Pressable>

        <View style={styles.logoContainer}>
          <Text style={styles.logoText}>K</Text>
        </View>

        <Pressable onPress={() => router.back()} style={styles.headerButton}>
          <ArrowRight color="#F8FAFC" size={24} />
        </Pressable>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.articleHeader}>
          <View style={styles.categoryBadge}>
            <Text style={styles.categoryText}>{getCategoryName(article.category)}</Text>
          </View>

          <View style={styles.metaRow}>
            <View style={styles.metaItem}>
              <Eye size={16} color="#64748B" />
              <Text style={styles.metaText}>{article.view_count || 0} צפיות</Text>
            </View>
            <View style={styles.metaDivider} />
            <View style={styles.metaItem}>
              <Clock size={16} color="#64748B" />
              <Text style={styles.metaText}>זמן קריאה: 5 דק׳</Text>
            </View>
          </View>
        </View>

        <Text style={styles.title}>{article.title}</Text>

        <Text style={styles.heroIntro}>{article.hero_intro}</Text>

        {/* SEO/GEO FIX: concise fact block for faster answer extraction by AI/search systems. */}
        <View style={styles.quickFactsBox}>
          <Text style={styles.quickFactsTitle}>תקציר מהיר</Text>
          <Text style={styles.quickFactItem}>נושא: {getCategoryName(article.category)}</Text>
          <Text style={styles.quickFactItem}>קהל יעד: מתחילים ומתקדמים הבוחרים קורס בתחום</Text>
          <Text style={styles.quickFactItem}>פורמט: מדריך פרקטי עם צעדים ישימים</Text>
        </View>

        <View style={styles.tldrBox}>
          <Text style={styles.tldrTitle}>התשובה הקצרה</Text>
          <Text style={styles.tldrText}>{article.tldr}</Text>
        </View>

        {article.main_content && Array.isArray(article.main_content) && article.main_content.map((section, index) => (
          <View key={index} style={styles.section}>
            <Text style={styles.sectionHeading}>{section.heading}</Text>
            <Text style={styles.sectionContent}>{section.content}</Text>
          </View>
        ))}

        {article.common_mistakes && (
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>טעויות נפוצות</Text>
            <Text style={styles.sectionContent}>{article.common_mistakes}</Text>
          </View>
        )}

        {article.how_to_choose && (
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>איך לבחור נכון</Text>
            <Text style={styles.sectionContent}>{article.how_to_choose}</Text>
          </View>
        )}

        {article.kursi_connection && (
          <View style={styles.kursiBox}>
            <Text style={styles.kursiText}>{article.kursi_connection}</Text>
          </View>
        )}

        <Pressable style={styles.ctaButton} onPress={handleCTA}>
          <Text style={styles.ctaButtonText}>{article.cta_text}</Text>
        </Pressable>
      </ScrollView>
    </View>
  );
}

function getCategoryName(category: string): string {
  const names: { [key: string]: string } = {
    general: 'כללי',
    tech: 'הייטק',
    qa: 'QA',
    digital: 'דיגיטל',
    stocks: 'שוק ההון',
    ai: 'בינה מלאכותית',
    data: 'דאטה',
    comparison: 'השוואה',
    audience: 'קהל יעד',
    career: 'קריירה',
  };
  return names[category] || category;
}

const _PAPER = '#F2EBDD';
const _PAPER_SOFT = '#FAF5EA';
const _INK = '#1A1611';
const _INK_SOFT = '#3A332A';
const _INK_MUTED = '#7A6F5F';
const _ACCENT = '#C8531E';
const _SERIF_BLACK = 'FrankRuhlLibre-Black';
const _SERIF_BOLD = 'FrankRuhlLibre-Bold';
const _SERIF = 'FrankRuhlLibre-Regular';
const _SANS = 'Heebo-Regular';
const _SANS_BOLD = 'Heebo-Bold';
const _MONO = 'SpaceMono-Regular';

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: _PAPER },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: Platform.OS === 'ios' ? 60 : 20,
    paddingBottom: 18,
    backgroundColor: _PAPER,
    borderBottomWidth: 1, borderBottomColor: _INK,
  },
  logoContainer: {
    width: 36, height: 36, borderWidth: 1, borderColor: _INK,
    justifyContent: 'center', alignItems: 'center', backgroundColor: _INK,
  },
  logoText: { fontSize: 20, fontFamily: _SERIF_BLACK, color: _PAPER },
  headerButton: { padding: 8 },

  articleHeader: { marginBottom: 24 },
  quickFactsBox: {
    backgroundColor: _PAPER_SOFT,
    borderWidth: 1, borderColor: _INK,
    padding: 22, marginBottom: 24,
  },
  quickFactsTitle: { color: _ACCENT, fontFamily: _MONO, fontSize: 11, marginBottom: 12, textAlign: 'right', letterSpacing: 1.6, textTransform: 'uppercase' as any },
  quickFactItem: { color: _INK, fontFamily: _SANS, fontSize: 15, lineHeight: 24, textAlign: 'right' },

  metaRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 16, marginTop: 14 },
  metaItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  metaDivider: { width: 1, height: 12, backgroundColor: _INK },
  metaText: { fontSize: 11, fontFamily: _MONO, color: _INK_MUTED, letterSpacing: 1.4, textTransform: 'uppercase' as any },

  scrollView: { flex: 1 },
  scrollContent: { padding: 32, maxWidth: 820, alignSelf: 'center', width: '100%' } as any,
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  errorContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24 },
  errorText: { fontSize: 18, fontFamily: _SERIF_BOLD, color: _INK, textAlign: 'center', marginBottom: 24 },
  backButton: { backgroundColor: _INK, paddingVertical: 14, paddingHorizontal: 26, borderWidth: 1, borderColor: _INK },
  backButtonText: { fontSize: 14, fontFamily: _SANS_BOLD, color: _PAPER, letterSpacing: 0.3 },

  categoryBadge: { alignSelf: 'flex-end', borderWidth: 1, borderColor: _INK, paddingVertical: 5, paddingHorizontal: 12, backgroundColor: 'transparent' },
  categoryText: { fontSize: 11, fontFamily: _MONO, color: _ACCENT, letterSpacing: 1.6, textTransform: 'uppercase' as any },

  title: {
    fontSize: 44, fontFamily: _SERIF_BLACK, color: _INK,
    textAlign: 'right', marginBottom: 20, lineHeight: 50, letterSpacing: -1.2,
  },
  heroIntro: {
    fontSize: 20, fontFamily: _SERIF, color: _INK_SOFT,
    textAlign: 'right', lineHeight: 32, marginBottom: 28,
  },
  tldrBox: {
    backgroundColor: _PAPER_SOFT,
    borderWidth: 1, borderColor: _INK, padding: 22, marginBottom: 36,
  },
  tldrTitle: { fontSize: 11, fontFamily: _MONO, color: _ACCENT, textAlign: 'right', marginBottom: 10, letterSpacing: 1.6, textTransform: 'uppercase' as any },
  tldrText: { fontSize: 17, fontFamily: _SERIF, color: _INK, textAlign: 'right', lineHeight: 27, fontStyle: 'italic' as any },

  section: { marginBottom: 40, paddingTop: 22, borderTopWidth: 1, borderTopColor: _INK },
  sectionHeading: { fontSize: 28, fontFamily: _SERIF_BOLD, color: _INK, textAlign: 'right', marginBottom: 14, letterSpacing: -0.5 },
  sectionContent: { fontSize: 17, fontFamily: _SANS, color: _INK_SOFT, textAlign: 'right', lineHeight: 28 },

  kursiBox: {
    backgroundColor: _INK, padding: 28, marginBottom: 28,
  },
  kursiText: { fontSize: 17, fontFamily: _SERIF, color: _PAPER_SOFT, textAlign: 'right', lineHeight: 28, fontStyle: 'italic' as any },

  ctaButton: {
    backgroundColor: _ACCENT, paddingVertical: 18, paddingHorizontal: 28,
    borderWidth: 1, borderColor: _ACCENT, alignItems: 'center', marginBottom: 40,
  },
  ctaButtonText: { fontSize: 15, fontFamily: _SANS_BOLD, color: _PAPER, letterSpacing: 0.4 },
});
